import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Box,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useParams } from 'react-router-dom';

import { deleteRelatedEntity } from 'common/api';
import { ReactComponent as Delete } from 'common/assets/images/delete-icon.svg';
import { ReactComponent as Plus } from 'common/assets/images/Plus.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import { MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	ENTITY_TYPE,
	IMPACT_PARENT_OBJECT_TYPE,
	ENTITY_STATUS,
	RELATED_OBJECT_TYPE,
} from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';
import { setMappingDrawer } from 'common/store/reducer/commonReducer';
import { setShouldHorizonDetailRefresh } from 'common/store/reducer/horizonScanReducer';
import {
	setGrammarCheckCompleted,
	setIsImpactUpdated,
	setTabIndex,
	setTaxonomyDetailsTabIndex,
} from 'common/store/reducer/mappingDetailsReducer';
import { pascalFormat, toSentenceCase } from 'common/utils/utilFunctions';

import { renderBasedOnEntityStatus } from '../Overview/OverviewUtils';

import {
	getImpactData,
	createImpactDataUsingCIP,
	updateImpactForWorkflow,
	getRelationshipTaskData,
} from '../../../api';

import TextEdit from './TextEdit';

import styles from './Impact.module.scss';

const { deleteFillColor, addFillColor } = styles;

const { IMPACTED, NOT_IMPACTED } = ENTITY_STATUS;

const { OK, CREATED } = HTTP_STATUS_CODES;

interface IImpactProps {
	pagemodule: string;
	subModuleType?: string;
	isDeleteEnabled?: boolean;
}

const Impact = (props: IImpactProps) => {
	const location = useLocation();
	const { state } = location;
	const { pagemodule, subModuleType, isDeleteEnabled } = props;
	const { groupId, subPartId } = useParams();
	const parentObjectId =
		state && state.entityId !== undefined
			? state.entityId
			: groupId || subPartId;
	const parentObjectType =
		state && state.entityId !== undefined
			? IMPACT_PARENT_OBJECT_TYPE.CITAION
			: IMPACT_PARENT_OBJECT_TYPE.CITATIONGROUP;
	const [impactValue, setImpactValue] = useState<string>('');
	const [impactLevel, setImpactLevel] = useState<string>('');
	const [updatedSummary, setUpdatedSummary] = useState<string>('');
	const [clicked, setClicked] = useState<boolean>(false);
	const [isSaveDisabled, setSaveDisabled] = useState<boolean>(false);
	const [impactData, setImpactData] = useState({
		comments: '',
		impactLevel: '',
		impactAssessment: '',
	});
	const [, setHasTasks] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);
	const selectedEntity = useSelector(
		(state: RootState) => state.common.selectedEntityTypeData,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const dispatch = useDispatch();
	const { id, type, generateMode, aiScore } = selectedEntity;
	// get api call to populate the data
	const fetchImpactData = async () => {
		if (type) {
			const updatedType =
				pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS &&
				type === ENTITY_TYPE.OBLIGATION
					? ENTITY_TYPE.GROUP
					: type;

			let impactParentType: string = ENTITY_TYPE.CITATION;
			if (pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS) {
				impactParentType = RELATED_OBJECT_TYPE.HORIZON;
			} else if (pagemodule === MODULES.REQUIREMENT_INVENTORY) {
				impactParentType = ENTITY_TYPE.GROUP;
			}
			const objectId =
				pagemodule === MODULES.REQUIREMENT_INVENTORY
					? selectedEntity.parentId || ''
					: parentObjectId;
			const response: any = await getImpactData(
				id,
				pascalFormat(impactParentType),
				objectId,
				pascalFormat(updatedType),
			);
			if (response?.statusCode === OK) {
				const { responseBody } = response;
				const { impactLevel, impactAssessment, comments } = responseBody;
				setImpactValue(impactAssessment);
				setImpactLevel(impactLevel);
				setUpdatedSummary(comments);
				setImpactData({ comments, impactLevel, impactAssessment });
				setSaveDisabled(true);
			} else {
				setImpactValue('');
				setImpactLevel('');
				setUpdatedSummary('');
			}
		}
	};
	// trigger the get api
	useEffect(() => {
		fetchImpactData();
		fetchTasks();
	}, [id]);
	const handleAssessmentItemChange = useCallback((event: any) => {
		setImpactValue(event.target.value);
	}, []);

	useEffect(() => {
		if (impactValue === NOT_IMPACTED) {
			setImpactLevel('');
			setUpdatedSummary('');
		}
	}, [impactValue]);

	const handleLevelChange = useCallback((event: any) => {
		setImpactLevel(event.target.value);
	}, []);

	useEffect(() => {
		// Temporarily removing conditional check for task. Once UX design is ready will handle accordingly.
		// if (pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS && impactValue === IMPACTED) {
		//	setSaveDisabled(true);
		// } else
		if (
			impactData.impactAssessment === impactValue &&
			impactData.impactLevel === impactLevel &&
			impactData.comments === updatedSummary
		) {
			setSaveDisabled(true);
		} else if (
			impactValue === NOT_IMPACTED &&
			!updatedSummary &&
			!impactLevel
		) {
			setSaveDisabled(false);
		} else if ((impactValue === IMPACTED && !impactLevel) || !updatedSummary) {
			setSaveDisabled(true);
		} else {
			setSaveDisabled(false);
		}
	}, [impactValue, impactLevel, updatedSummary, impactData]);

	const fetchTasks = async () => {
		const payload = {
			sortField: 'targetEndDate',
			sortOrder: 'DESC',
			andCriterias: [
				{
					field: 'relatedParentObjectId',
					value: subPartId || '',
					operation: 'EQUAL',
				},
				{
					field: 'assignedObjectId',
					value: id || '',
					operation: 'EQUAL',
				},
			],
		};

		const response = await getRelationshipTaskData(payload);
		if (response?.statusCode === OK) {
			const taskLen = response?.responseBody?.records?.length;
			if (taskLen && taskLen > 0) {
				setHasTasks(true);
			} else {
				setHasTasks(false);
			}
		} else {
			setHasTasks(false);
		}
	};

	const saveHandler = useCallback(async () => {
		const isCip = process.env.REACT_APP_IS_CIP === 'true';
		setClicked(true);

		if (!type) {
			return;
		}

		if (impactValue === '') {
			return;
		}

		let response;
		const userId = loggedInUser?.userId;
		if (pagemodule === MODULES.REQUIREMENT_INVENTORY) {
			const regInvHeaders = {
				impactLevel,
				impactObjectType: pascalFormat(type),
				impactObjectId: id,
				loggedInUser: userId,
				comments: updatedSummary,
				parentObjectId: selectedEntity.parentId,
				reviewStatus: impactValue,
				generateMode,
				aiScore,
				impactType: impactValue,
				parentObjectType: 'CitationGroupBusinessUnit',
				citationGroupBusinessUnitIds:
					selectedEntity.citationGroupBusinessUnitIds,
			};
			setIsLoading(true);
			response = await updateImpactForWorkflow(regInvHeaders, id);
			setIsLoading(false);
			parsePostResponse(response);
			return;
		}

		if (pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS) {
			const horizonImpactData = {
				reviewStatus: impactValue,
				impactType: impactValue,
				loggedInUser: userId,
				parentObjectId,
				parentObjectType: RELATED_OBJECT_TYPE.HORIZON,
				impactLevel,
				comments: updatedSummary,
				generateMode,
				aiScore,
				impactObjectId: id,
				impactObjectType:
					pascalFormat(type) === ENTITY_TYPE.OBLIGATION
						? ENTITY_TYPE.GROUP
						: pascalFormat(type),
			};
			setIsLoading(true);
			response = await updateImpactForWorkflow(horizonImpactData, id);
			setIsLoading(false);
		}
		if (pagemodule === MODULES.RISK_REFERENCE_TAXONOMY) {
			let childId = id;
			let parentId = parentObjectId;
			if (selectedEntity?.swap) {
				childId = parentId;
				parentId = id;
			}
			const impactData = {
				impactLevel,
				impactObjectType: pascalFormat(type),
				impactObjectId: childId,
				impactType: impactValue,
				loggedInUser: userId,
				comments: updatedSummary,
				parentObjectId: parentId,
				parentObjectType,
				reviewStatus: impactValue,
			};
			setIsLoading(true);
			response = isCip
				? await createImpactDataUsingCIP(impactData, parentId)
				: await updateImpactForWorkflow(impactData, id);
			setIsLoading(false);
		}
		parsePostResponse(response);
	}, [
		id,
		impactLevel,
		updatedSummary,
		impactData,
		impactValue,
		sideDrawerState,
	]);

	const onCancel = useCallback(() => {
		setClicked(true);
		setImpactValue('');
		setImpactLevel('');
		setUpdatedSummary('');
		dispatch(setMappingDrawer(!sideDrawerState));
		dispatch(setTaxonomyDetailsTabIndex('overview'));
	}, [sideDrawerState]);
	const parsePostResponse = (resp: any) => {
		if (resp?.statusCode === OK || resp?.statusCode === CREATED) {
			if (pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS) {
				// Temporarily removing conditional check for task. Once UX design is ready will handle accordingly.
				dispatch(setShouldHorizonDetailRefresh(true));
				renderBasedOnEntityStatus(impactValue, dispatch, sideDrawerState);
			}
			if (pagemodule === MODULES.RISK_REFERENCE_TAXONOMY) {
				if (impactValue === IMPACTED) {
					dispatch(setTabIndex('tasks'));
				} else {
					dispatch(setMappingDrawer(!sideDrawerState));
					dispatch(setTaxonomyDetailsTabIndex('overview'));
				}
			}
			if (pagemodule === MODULES.REQUIREMENT_INVENTORY) {
				dispatch(setMappingDrawer(!sideDrawerState));
			}
			setImpactValue('');
			setImpactLevel('');
			setUpdatedSummary('');
			dispatch(setIsImpactUpdated(true));
			dispatch(setGrammarCheckCompleted(false));
		}
	};

	const deleteButtonClicked = async () => {
		let parentId = parentObjectId;
		let tempId = id;
		if (
			pagemodule === MODULES.REQUIREMENT_INVENTORY ||
			MODULES.HORIZON_SCAN_AND_ALERTS
		) {
			parentId = selectedEntity.parentId;
		}
		if (pagemodule === MODULES.RISK_REFERENCE_TAXONOMY) {
			if (selectedEntity.swap) {
				tempId = parentId;
				parentId = id;
			} else {
				parentId = parentObjectId || state.entityId;
			}
		}
		setIsDeleting(true);
		const response = await deleteRelatedEntity(
			tempId,
			parentId || '',
			pagemodule,
			loggedInUser?.email || '',
		);
		setIsDeleting(false);
		if (response?.statusCode === OK || response?.statusCode === CREATED) {
			dispatch(setMappingDrawer(!sideDrawerState));
		}
	};

	const checkIfDeleteButtonDisabled = () => {
		if (selectedEntity.generateMode === 'AI') {
			return !isDeleteEnabled || selectedEntity.reviewStatus === 'Reviewed';
		}
		return (
			selectedEntity.createdBy !== loggedInUser?.userId ||
			selectedEntity.reviewStatus === 'Reviewed'
		);
	};

	return (
		<div
			className={styles['impact-main-div']}
			data-testid="impact-main-container"
		>
			<Stack direction="row" spacing={5}>
				<FormControl
					variant="filled"
					className={styles['impact-tab-formcontrol']}
				>
					<InputLabel
						className={styles['impact-tab-label']}
						data-testid="impact-assessment-label"
					>
						{toSentenceCase('Impact Assessment')}
					</InputLabel>
					<Select
						className={styles['impact-tab-item']}
						onChange={handleAssessmentItemChange}
						value={impactValue}
						IconComponent={ExpandMoreRoundedIcon}
						inputProps={{
							'data-testid': 'impact-assessment-dropdown',
						}}
						defaultValue={impactValue}
					>
						<MenuItem value={IMPACTED} data-testid="impacted">
							{IMPACTED}
						</MenuItem>
						<MenuItem value={NOT_IMPACTED}>{NOT_IMPACTED}</MenuItem>
					</Select>
				</FormControl>
				{impactValue === IMPACTED && (
					<FormControl
						variant="filled"
						className={styles['impact-tab-formcontrol']}
					>
						<InputLabel className={styles['impact-tab-label']}>
							{toSentenceCase('Impact Level')}
						</InputLabel>
						<Select
							className={styles['impact-tab-item']}
							onChange={handleLevelChange}
							IconComponent={ExpandMoreRoundedIcon}
							value={impactLevel}
							inputProps={{
								'data-testid': 'impact-level-dropdown',
							}}
							defaultValue={impactLevel}
						>
							<MenuItem value="high">High</MenuItem>
							<MenuItem value="medium">Medium</MenuItem>
							<MenuItem value="low">Low</MenuItem>
						</Select>
					</FormControl>
				)}
			</Stack>
			{impactValue === IMPACTED && (
				<TextEdit
					impactData={impactData}
					updatedSummary={updatedSummary}
					setUpdatedSummary={setUpdatedSummary}
					clicked={clicked}
					setClicked={setClicked}
				/>
			)}
			<Stack
				className={styles['impact-btn-all']}
				direction="row"
				justifyContent="flex-end"
				gap="1rem"
			>
				{(pagemodule !== MODULES.REQUIREMENT_INVENTORY ||
					subModuleType === MODULES.HORIZON_SCAN_AND_ALERTS) && (
					<Stack
						direction="row"
						justifyContent="flex-end"
						className={styles['impact-btn-grp']}
						gap="1rem"
					>
						{/* Temporarily removing conditional check for task. Once UX design is ready will handle accordingly.
					{pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS && impactValue === IMPACTED && !hasTasks ? <div className={styles['alert-message-wrapper']} data-testid="alert-message-wrapper">
						{toSentenceCase('Impacts and Tasks need to be addressed in order to save.')}
					</div> : ''} */}
						<ButtonComp
							btnType="secondary"
							clickHandler={onCancel}
							data-testid="impact-cancel-btn"
						>
							Cancel
						</ButtonComp>
						<ButtonComp
							clickHandler={saveHandler}
							compClass={styles['save-btn']}
							btnType="primary"
							disabled={isSaveDisabled}
							data-testid="impact-save-btn"
							loading={isLoading}
						>
							Save
						</ButtonComp>
					</Stack>
				)}
				{(pagemodule === MODULES.REQUIREMENT_INVENTORY ||
					MODULES.HORIZON_SCAN_AND_ALERTS ||
					pagemodule === MODULES.RISK_REFERENCE_TAXONOMY) &&
					!subModuleType && (
						<Stack
							direction="row"
							justifyContent="flex-end"
							className={styles['impact-btn-grp']}
							gap="1rem"
						>
							<ButtonComp
								btnType="secondary"
								disabled={
									pagemodule === MODULES.REQUIREMENT_INVENTORY ||
									pagemodule === MODULES.HORIZON_SCAN_AND_ALERTS
										? checkIfDeleteButtonDisabled()
										: pagemodule === MODULES.RISK_REFERENCE_TAXONOMY &&
										  selectedEntity.createdBy !== loggedInUser?.userId
								}
								startIcon={
									!isDeleting && (
										<Box className={styles['impact-tab-button-icon']}>
											<Delete fill={deleteFillColor} />
										</Box>
									)
								}
								loading={isDeleting}
								clickHandler={deleteButtonClicked}
								data-testid="impact-delete-btn"
							>
								Delete
							</ButtonComp>
							{pagemodule === MODULES.REQUIREMENT_INVENTORY && (
								<ButtonComp
									clickHandler={saveHandler}
									compClass={styles['save-btn']}
									startIcon={
										<Box className={styles['impact-tab-button-icon']}>
											<Plus fill={addFillColor} />
										</Box>
									}
									btnType="primary"
									disabled={
										pagemodule === MODULES.REQUIREMENT_INVENTORY ||
										MODULES.HORIZON_SCAN_AND_ALERTS
											? isSaveDisabled || checkIfDeleteButtonDisabled()
											: isSaveDisabled
									}
									data-testid="impact-add-btn"
								>
									Add
								</ButtonComp>
							)}
						</Stack>
					)}
			</Stack>
		</div>
	);
};
export default Impact;
